import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

// Extend Navigator interface to access connection info
interface NavigatorConnection extends Navigator {
  connection?: {
    effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | '5g';
    saveData: boolean;
  };
}

@Injectable({ providedIn: 'root' })
export class NetworkAwarePreloadStrategy implements PreloadingStrategy {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Ensure we're in the browser
    if (!isPlatformBrowser(this.platformId)) {
      return of(null); // SSR or prerendering - skip preload
    }

    // Route-level opt-out
    if (route.data?.['preload'] === false) {
      return of(null);
    }

    // Access navigator safely
    const navigatorConnection =
      typeof navigator !== 'undefined'
        ? (navigator as NavigatorConnection)
        : null;

    if (navigatorConnection?.connection) {
      const { saveData, effectiveType } = navigatorConnection.connection;

      if (saveData) {
        return of(null); // Respect user's data saving settings
      }

      if (effectiveType === '4g' || effectiveType === '5g') {
        return load(); // Fast connection: preload
      }

      return of(null); // Slow or unknown connection: skip
    }

    // If Network Information API is not supported, fallback to skip preload
    return of(null); // Or change to `return load();` to preload by default
  }
}
